'use client'
import { useGifsWithPlaceholder, useGifUpdater } from '@/api/hooks'
import { gridPathnameMap } from '@/app/util/ads-endpoint'
import { comscoreConfig } from '@/app/util/comscore'
import { cn } from '@/util/cn'
import { IGif } from '@giphy/js-types'
import { Grid as SDKGrid } from '@giphy/react-components'
import { getFirstPathname } from 'analytics/src/utils/page'
import { CS_SIDEBAR_WIDTH, SIDEBAR_WIDTH } from 'edit/panel/constants'
import dynamic from 'next/dynamic'
import { usePathname, useRouter } from 'next/navigation'
import {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import useWindowSize from 'react-use/lib/useWindowSize'
import styled from 'styled-components'
import { Icon } from 'ui/src/components/logo'
import { desktopWidth } from 'ui/src/constants'
import { desktop } from 'ui/src/css'
import UAParserContext from '../../context/ua-parser'
import { relativeUrl } from '../../util/url'
import useClientRender from '../../util/use-client-render'
import BulkEditButton from './bulk-edit-button'
import { useBulkEditOpen } from './bulk-edit-state'
import { usePageViewTracking } from './use-page-view-tracking'

const GifOverlay = dynamic(() => import('./overlay/gif-overlay'), { ssr: false })

type Props = Pick<SDKGrid['props'], 'initialGifs' | 'fetchGifs' | 'onGifClick' | 'overlay' | 'columnOffsets'> & {
    gaTrackingUrl?: (page: number) => string
    noShowMessage?: ReactNode
    width?: number
    columns?: number
    externalGifs?: IGif[]
    setExternalGifs?: Dispatch<SetStateAction<IGif[]>>
    blockPlaceholder?: boolean
}

type GridContextProps = {
    gifs: IGif[]
    setGifs: Dispatch<SetStateAction<IGif[]>>
}

export const GridContext = createContext({
    gifs: [],
    setGifs: () => {},
} as GridContextProps)

const NoGifsMessage = styled.p`
    text-align: center;
    padding: 10px;
`

export function useExternalGifs(
    initialGifs: IGif[],
    hoistedGifs?: IGif[],
    hoistedState?: Dispatch<SetStateAction<IGif[]>>
) {
    const [_externalGifs, _setExternalGifs] = useState<IGif[]>(initialGifs)
    return [hoistedGifs || _externalGifs, hoistedState || _setExternalGifs] as const
}

const defaultMobileWindowWidth = 375
const Grid = ({
    initialGifs: _initialGifs = [],
    fetchGifs,
    gaTrackingUrl,
    width,
    columns,
    columnOffsets,
    onGifClick,
    overlay,
    noShowMessage,
    externalGifs: _externalGifs,
    setExternalGifs: _setExternalGifs,
    blockPlaceholder = false,
}: Props) => {
    const [hasGifsToShow, setHasGifsToShow] = useState(true)
    const router = useRouter()
    const pathname = usePathname()
    // create a placeholder id
    const placeholderId = useRef(`geotarget-placeholder-${pathname}`)
    const gridViewName = gridPathnameMap[getFirstPathname(usePathname())]
    // add a placeholder gif with that id
    const initialGifs = useGifsWithPlaceholder(_initialGifs, placeholderId.current, gridViewName, blockPlaceholder)
    // set external gifs to be passed to grid
    let [externalGifs, setExternalGifs] = useExternalGifs(initialGifs, _externalGifs, _setExternalGifs)
    // when geotargeted ad loads on client, replace placeholder with ad
    externalGifs = useGifUpdater(externalGifs, placeholderId.current, gridViewName)

    const [gifCount, setGifCount] = useState(0)
    usePageViewTracking(gaTrackingUrl, gifCount)
    const { deviceType, isBot } = useContext(UAParserContext)
    // for dynamic search
    // const { searchKey } = useContext(SearchContext)
    let { width: windowWidth } = useWindowSize(defaultMobileWindowWidth)
    // adjust window width for curation station to allow for sidebar
    const isEditGifs = pathname.includes('/edit/gifs')
    // no way to do grids on mobile yet since we don't know window width on server
    const clientRender = useClientRender()
    const hasRendered = useRef(new Set([windowWidth]))
    const [mobileWidth, setMobileWidth] = useState(defaultMobileWindowWidth)
    useEffect(() => {
        if (hasRendered.current.size > 2) {
            // don't rerender automatically, wait till browser resize
            setMobileWidth(windowWidth)
        }
        hasRendered.current.add(windowWidth)
    }, [windowWidth])

    const canRender = clientRender || (isBot && /^(\/explore\/|\/$)/.test(pathname))
    // go by server ua parsing
    let isDesktop = deviceType === 'desktop'
    // switch to window width on client
    if (canRender) {
        isDesktop = windowWidth >= desktop.breakpointWidth
    }
    // fetch gifs in advance of 250px so you don't see the loader if you slow scroll
    const loaderConfig = useMemo(() => (canRender ? { rootMargin: '0px 0px 250px 0px' } : undefined), [canRender])
    const eagerLoadingIds = useMemo(() => initialGifs?.map((gif) => gif.id), [initialGifs])
    const bulkEditOpen = useBulkEditOpen()
    if (!hasGifsToShow && externalGifs.length === 0) {
        return noShowMessage ? <NoGifsMessage>{noShowMessage}</NoGifsMessage> : null
    }
    const bulkEditColumns = isEditGifs ? 4 : windowWidth > 1040 ? 5 : 4
    return (
        <GridContext.Provider value={{ gifs: externalGifs, setGifs: setExternalGifs }}>
            {isEditGifs && (
                <div className="flex justify-between items-center  w-full mb-3">
                    <div className="flex items-center z-20 w-full" style={{ minHeight: 50 }}>
                        <Icon />

                        <h1 className="text-2xl font-light text-giphyWhite ml-2">Curation Station</h1>
                    </div>
                </div>
            )}
            <BulkEditButton />
            <div
                className={cn(
                    bulkEditOpen && isEditGifs
                        ? 'bg-giphyBlack z-modal fixed inset-0 flex justify-center overflow-y-auto mt-19'
                        : bulkEditOpen && 'bg-giphyBlack z-modal fixed inset-0 flex justify-center overflow-y-auto'
                )}
                style={
                    bulkEditOpen
                        ? isEditGifs
                            ? { marginRight: SIDEBAR_WIDTH, marginLeft: CS_SIDEBAR_WIDTH }
                            : bulkEditOpen
                              ? { marginRight: SIDEBAR_WIDTH }
                              : {}
                        : {}
                }
            >
                <SDKGrid
                    columns={bulkEditOpen ? bulkEditColumns : columns || (isDesktop ? 4 : 2)}
                    width={
                        bulkEditOpen
                            ? isEditGifs
                                ? Math.min(windowWidth - (SIDEBAR_WIDTH + CS_SIDEBAR_WIDTH), 1440) - 12 * 2
                                : Math.min(windowWidth - SIDEBAR_WIDTH, 1440) - 12 * 2
                            : width || (isDesktop ? desktopWidth : mobileWidth)
                    }
                    percentWidth={isDesktop ? undefined : '100%'}
                    fetchGifs={async (offset) => {
                        const result = await fetchGifs(offset)
                        if (window.COMSCORE?.beacon) {
                            window.COMSCORE.beacon(comscoreConfig)
                            fetch('/comscore-page-view')
                        }
                        if (result.data.length === 0 && initialGifs?.length === 0) {
                            setHasGifsToShow(false)
                        }
                        return result
                    }}
                    externalGifs={externalGifs}
                    initialGifs={initialGifs}
                    eagerIds={eagerLoadingIds}
                    onGifClick={(gif, event) => {
                        if (!onGifClick) {
                            event.preventDefault()
                            event.stopPropagation()
                            router.push(relativeUrl(gif.url))
                        } else {
                            onGifClick(gif, event)
                        }
                    }}
                    gutter={isDesktop ? 12 : undefined}
                    overlay={overlay || (isDesktop ? GifOverlay : undefined)}
                    hideAttribution
                    loaderConfig={loaderConfig}
                    onGifsFetched={(gifs) => {
                        setGifCount(gifs.length)
                        setExternalGifs(gifs)
                    }}
                    columnOffsets={columnOffsets}
                />
            </div>
        </GridContext.Provider>
    )
}

export default Grid
